import {
  resolveCancelAction,
  type PaymentCancelAction,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import {
  useIsDomesticWireDualApprovalRequired,
  useIsInternationalWireDualApprovalRequired,
  useIsACHDualApprovalRequired,
  useUser,
} from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import type { PaymentMetadataProps } from "./PaymentActivityDrawer";

export type PaymentActions = {
  showCancelButton: boolean;
  showReverseButton: boolean;
  cancelAction: PaymentCancelAction | null;
};

// gets the institution/user-level values that gate payment cancel and reverse actions
const usePaymentActions = () => {
  const currentUser = useUser();
  const isDomesticWireDualApprovalRequired =
    useIsDomesticWireDualApprovalRequired();
  const isInternationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const isAchReversalDualApprovalRequired = useIsACHDualApprovalRequired();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const institution = useInstitutionSettings();

  const getActions = (
    payment: API.FormattedPaymentActivity,
    // rail-specific detail from the drawer fetch; absent in list contexts
    paymentMetadata?: PaymentMetadataProps | null
  ): PaymentActions => {
    const cancelAction = resolveCancelAction(payment, {
      currentUserUuid: currentUser?.id,
      isDomesticWireDualApprovalRequired,
      isInternationalWireDualApprovalRequired,
      isAchReversalDualApprovalRequired,
      isHoldCancelable: paymentMetadata?.is_hold_cancelable === true,
      isScheduleCancelable: paymentMetadata?.is_cancelable === true,
    });

    const hasReversePermission = payment.isCollectPayment
      ? hasReverseCollectPermission
      : hasReverseSendPermission;

    const showReverseButton = Boolean(
      institution?.features?.user_initiated_ach_reversals &&
        payment.isReversible &&
        hasReversePermission
    );

    return {
      showCancelButton: cancelAction !== null,
      showReverseButton,
      cancelAction,
    };
  };

  return { hasDualApprovalPermission, getActions };
};

export default usePaymentActions;
